import { Link } from 'react-router-dom';
import { Check, Star, ArrowRight, Phone, Flame } from 'lucide-react';
import { IMG, PACKAGES, ADDONS, CONTACT, PREMIUM_KEYS, packagePrice } from '../data';

function PriceCell({ label, pkgKey, vehType }) {
  const { original, discounted, promo } = packagePrice(pkgKey, vehType);
  return (
    <div className="bg-black/30 border border-white/5 rounded-xl p-4">
      <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</div>
      {promo ? (
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl text-white">${discounted}</span>
          <span className="text-zinc-500 line-through text-sm">${original}</span>
        </div>
      ) : (
        <div className="font-display text-2xl">${original}</div>
      )}
    </div>
  );
}

function ServiceCard({ pkgKey, p, image, highlight }) {
  const isPromo = PREMIUM_KEYS.has(pkgKey);
  return (
    <div
      data-testid={`service-${pkgKey}`}
      className={`relative rounded-3xl overflow-hidden border ${highlight ? 'border-blue-500/40 bg-gradient-to-br from-blue-600/10 to-transparent shadow-[0_0_40px_rgba(37,99,235,0.15)]' : 'border-white/10 bg-[#141414]'}`}
    >
      {isPromo && (
        <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-amber-500 to-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
          <Flame className="w-3.5 h-3.5" /> Limited Time – 20% OFF <Flame className="w-3.5 h-3.5" />
        </div>
      )}
      {image && <img src={image} alt={p.name} className="w-full h-72 object-cover" />}
      <div className="p-8 md:p-10">
        {highlight && !isPromo && <span className="pill !bg-blue-600 !text-white !border-blue-600 mb-4"><Star className="w-3 h-3" /> Most Popular</span>}
        <h3 className="font-display text-3xl md:text-4xl font-medium mb-2">{p.name}</h3>
        <p className="text-zinc-400 mb-6">{p.tagline}</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
          <PriceCell label="Sedan / Coupe" pkgKey={pkgKey} vehType="sedan" />
          <PriceCell label="Truck / SUV" pkgKey={pkgKey} vehType="truck" />
          <PriceCell label="3-Row SUV / Minivan" pkgKey={pkgKey} vehType="suv3row" />
        </div>

        <h4 className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4 font-bold">Includes</h4>
        <ul className="space-y-2.5 mb-8">
          {p.includes.map((inc) => (
            <li key={inc} className="flex items-start gap-3 text-sm text-zinc-300">
              <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" /> {inc}
            </li>
          ))}
        </ul>

        <Link
          to={`/book?pkg=${pkgKey}`}
          data-testid={`service-book-${pkgKey}`}
          className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${highlight ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border border-white/20 hover:bg-white/10 text-white'}`}
        >
          Book Now <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function ServiceSection({ pill, title, subtitle, cards }) {
  return (
    <div className="mt-16">
      <span className="pill mb-4">{pill}</span>
      <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight">{title}</h2>
      {subtitle && <p className="text-zinc-400 max-w-2xl mt-3">{subtitle}</p>}
      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        {cards.map((c) => <ServiceCard key={c.pkgKey} {...c} />)}
      </div>
    </div>
  );
}

export default function Services() {
  const fullDetail = [
    { pkgKey: 'regular', p: PACKAGES.regular, image: IMG.regularDetail, highlight: false },
    { pkgKey: 'premium', p: PACKAGES.premium, image: IMG.luxury1, highlight: true },
  ];
  const interiorOnly = [
    { pkgKey: 'interior_regular', p: PACKAGES.interior_regular, image: IMG.premiumInterior, highlight: false },
    { pkgKey: 'interior_premium', p: PACKAGES.interior_premium, image: IMG.lexusInterior, highlight: true },
  ];
  const exteriorOnly = [
    { pkgKey: 'exterior_regular', p: PACKAGES.exterior_regular, image: IMG.carWash, highlight: false },
    { pkgKey: 'exterior_premium', p: PACKAGES.exterior_premium, image: IMG.premiumExterior, highlight: true },
  ];

  return (
    <div data-testid="services-page" className="pt-32 pb-24 bg-[#0a0a0a] text-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <span className="pill mb-4">Our Services · 3+ Years Of Detailing</span>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter max-w-3xl">Detailing, perfected.</h1>
        <p className="text-zinc-400 max-w-2xl mt-5 text-lg">From everyday refresh to flagship transformation — cars, trucks, SUVs, semi trucks, and boats. Every service uses pro-grade tools and showroom standards.</p>

        <ServiceSection
          pill="Full Detail"
          title="Interior + Exterior together."
          subtitle="Our signature top-to-bottom detail — inside and out."
          cards={fullDetail}
        />

        <ServiceSection
          pill="Interior Only"
          title="Just the inside — refreshed."
          subtitle="Perfect for daily drivers, families, and anyone who spends time in their vehicle."
          cards={interiorOnly}
        />

        <ServiceSection
          pill="Exterior Only"
          title="Paint, wheels, and finish."
          subtitle="Great for maintaining a spotless look between full details."
          cards={exteriorOnly}
        />

        {/* FLEET */}
        <div data-testid="services-fleet" className="mt-16 rounded-3xl overflow-hidden border border-white/10 bg-[#141414] grid md:grid-cols-2">
          <img src={IMG.fleet} alt="Fleet detailing" className="w-full h-full object-cover min-h-[320px]" />
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <span className="pill mb-3">Fleet Detailing</span>
            <h2 className="font-display text-3xl md:text-4xl font-medium mb-3">Fleet & Business Detailing</h2>
            <p className="text-zinc-400 mb-3">Do you run a business with multiple company vehicles? We&apos;ll keep your entire fleet in showroom condition on a schedule that works for you.</p>
            <ul className="text-zinc-300 text-sm space-y-2 mb-6">
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-blue-400 mt-0.5" /> Company cars, work trucks, service vans</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-blue-400 mt-0.5" /> Semi trucks and boats also detailed</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-blue-400 mt-0.5" /> Volume pricing — quoted per fleet</li>
              <li className="flex items-start gap-3"><Check className="w-4 h-4 text-blue-400 mt-0.5" /> Recurring schedules available</li>
            </ul>
            <p className="text-zinc-400 text-sm mb-5">Fleet pricing is custom-tailored. <span className="text-white">Contact us for a quote</span> — text or call.</p>
            <div className="flex flex-wrap gap-3">
              <a href={`tel:${CONTACT.fleetPhoneRaw}`} data-testid="services-fleet-call" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full font-medium inline-flex items-center gap-2 hover:shadow-[0_0_30px_rgba(37,99,235,0.4)]"><Phone className="w-4 h-4" /> Call for Quote</a>
              <a href={`sms:${CONTACT.fleetPhoneRaw}`} data-testid="services-fleet-text" className="border border-white/20 hover:bg-white/10 text-white px-6 py-3 rounded-full font-medium">Text {CONTACT.fleetPhone}</a>
            </div>
          </div>
        </div>

        {/* ADD-ONS */}
        <div className="mt-24">
          <span className="pill mb-4">Enhance Your Detail</span>
          <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tight mb-12">Add-on services</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ADDONS.map((a) => (
              <div key={a.id} data-testid={`addon-${a.id}`} className="bg-[#141414] border border-white/5 rounded-2xl p-7 hover:border-blue-500/40 transition-all">
                <div className="font-display text-xl mb-3">{a.label}</div>
                <div className="text-blue-400 font-display text-3xl font-light">{a.price}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-zinc-500 text-sm mt-12 max-w-3xl">
          * Prices are starting rates and may vary depending on vehicle size, condition, and level of contamination.
        </p>
      </div>
    </div>
  );
}
